import { type ComponentProps, useMemo } from 'react'
import { Event } from '@cocoonspace/sdk-js/types/event'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Badge } from '@cocoonspace/ui/components/badge'
import {
	DateBadge,
	DateBadgeSkeleton,
} from '@cocoonspace/ui/components/cocoon/date-badge'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@cocoonspace/ui/components/tooltip'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'

import { VisioBadge } from '~/domains/scheduling/event-types/event_type_card/visio-badge'
import type { EventItem } from '../my-events/use-all-events.hook'

const EventTypeBadge = ({
	event,
}: {
	event: Pick<
		EventItem,
		| 'type'
		| 'isOrganizer'
		| 'isGuest'
		| 'isScheduler'
		| 'location'
		| 'googleCalendar'
	>
}) => {
	const { t } = useLingui()

	const badgeVariant = useMemo(() => {
		switch (event.type) {
			case 'quotation':
			case 'meeting':
				return 'border-primary text-primary bg-primary/5'
			case 'desk':
				return 'border-blue-500 text-blue-600 bg-blue-50'
			case 'visit':
				return 'border-gray-400 text-gray-600 bg-gray-50'
			default:
				return 'border-green-500 text-green-600 bg-green-50'
		}
	}, [event.type])

	const displayText = useMemo(() => {
		switch (event.type) {
			case 'quotation':
			case 'meeting':
				return t`Cocoon`
			case 'desk':
				return t`Desk`
			case 'visit':
				return t`Visite`
			default:
				return event.type
		}
	}, [event.type, t])

	return (
		<div className='flex flex-row items-center gap-2'>
			{event.isOrganizer && event.type && (
				<Badge
					variant='secondary'
					className={cn('border', badgeVariant)}
					size='sm'
				>
					{displayText}
				</Badge>
			)}

			{event.isGuest && (
				<Badge
					size='sm'
					variant='secondary'
				>
					{t`Invité·e`}
				</Badge>
			)}

			{!!event.googleCalendar.meetLink && (
				<VisioBadge
					size='sm'
					className={'border'}
				/>
			)}
		</div>
	)
}

export const EventFromTo = ({
	event,
}: {
	event: Pick<EventItem, 'startLocal' | 'endLocal' | 'timezone'>
}) => {
	const { t } = useLingui()

	const from = dateTimeUtils(`${event.startLocal}`)
		.tz(event.timezone)
		.customFormat('hours')

	const to = dateTimeUtils(`${event.endLocal}`)
		.tz(event.timezone)
		.customFormat('hours')

	return (
		<div className='flex flex-row items-center gap-1 text-sm'>
			{t`de ${from} à ${to}`}
		</div>
	)
}

export const EventTitle = ({ event }: { event: Pick<EventItem, 'title'> }) => {
	const { t } = useLingui()

	const eventTitle = event.title || t`Évènement Cocoon Space`

	return (
		<div
			title={eventTitle}
			className='truncate text-lg leading-none font-bold'
		>
			{eventTitle}
		</div>
	)
}

const EventLabelsBadge = ({ event }: { event: Event }) => {
	const labels = event.labels
	if (!labels || labels.length === 0) return null

	const firstLabel = labels[0]
	const remainingCount = labels.length - 1
	const remainingLabels = labels.slice(1)

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className='flex items-center gap-1'>
					<Badge
						size='sm'
						variant='outline'
						className='max-w-24'
					>
						<span className='truncate'>{firstLabel.name}</span>
					</Badge>

					{remainingCount > 0 && (
						<Badge
							variant='outline'
							size='sm'
							className='cursor-pointer'
							onClick={(e) => {
								e.stopPropagation()
							}}
						>
							+ {remainingCount}
						</Badge>
					)}
				</div>
			</TooltipTrigger>

			{remainingCount > 0 && (
				<TooltipContent className='p-2'>
					<div className='flex flex-wrap gap-1'>
						{remainingLabels.map((label) => (
							<Badge
								key={label.id}
								size='sm'
								variant='outline'
								className='max-w-24'
							>
								<span className='truncate'>{label.name}</span>
							</Badge>
						))}
					</div>
				</TooltipContent>
			)}
		</Tooltip>
	)
}

export const EventDateTimeInfos = ({
	event,
	altStyle,
	showSpaceTitle,
	...props
}: {
	event: Pick<
		EventItem,
		| 'title'
		| 'startLocal'
		| 'endLocal'
		| 'timezone'
		| 'deskQty'
		| 'type'
		| 'location'
		| 'isOrganizer'
		| 'isGuest'
		| 'isScheduler'
		| 'googleCalendar'
		| 'originalEvent'
	>
	altStyle?: boolean
	showSpaceTitle?: boolean
} & ComponentProps<'div'>) => {
	return (
		<div
			{...props}
			className={cn('flex items-center gap-4 overflow-x-auto', props.className)}
		>
			<DateBadge
				inverted={!altStyle}
				day={event.startLocal as string}
				tz={event.timezone}
			/>

			<div className='flex w-full flex-1 flex-col overflow-x-auto'>
				<EventTitle event={event} />
				<EventFromTo event={event} />

				{showSpaceTitle && (
					<div className='flex flex-row items-center gap-1 text-sm'>
						{event.location?.name}
					</div>
				)}

				<div className='flex min-w-0 gap-2 overflow-x-hidden'>
					<EventTypeBadge event={event} />

					{event.originalEvent && (
						<EventLabelsBadge event={event.originalEvent} />
					)}
				</div>
			</div>
		</div>
	)
}

EventDateTimeInfos.skeleton = ({
	className,
	...props
}: ComponentProps<'div'>) => (
	<div
		className={cn('flex flex-row items-center gap-4', className)}
		{...props}
	>
		<DateBadgeSkeleton />

		<div className='flex-1 overflow-x-hidden'>
			<Skeleton className='mb-1 h-[16px] w-[175px]' />
			<Skeleton className='h-[16px] w-[120px]' />
		</div>
	</div>
)
